import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type {
  AssistantActionOutcome,
  AssistantFailureReason,
  AssistantMessage,
  ConfirmationPresenter,
} from '@/stores/assistantStore';
import { boundQuoteResult, type QuoteResultAction } from '@/lib/quoteResultAction';
import { queryKeys } from './queryKeys';

/** Languages the portal assistant harness accepts (#2607). */
export const ASSISTANT_LANGUAGES = ['fi', 'sv', 'en'] as const;
export type AssistantLanguage = (typeof ASSISTANT_LANGUAGES)[number];

function parseAssistantLanguage(value: string | undefined | null): AssistantLanguage | undefined {
  if (typeof value !== 'string' || value.length === 0) return undefined;
  const base = value.trim().toLowerCase().split(/[-_]/)[0] ?? '';
  return (ASSISTANT_LANGUAGES as readonly string[]).includes(base)
    ? (base as AssistantLanguage)
    : undefined;
}

/**
 * Validated UI language, falling back to the user's preferred language, then fi.
 * `en-US` / `sv-SE` collapse to their base tag.
 */
export function assistantLanguageOf(
  uiLanguage: string | undefined,
  preferredLanguage?: string | null
): AssistantLanguage {
  return parseAssistantLanguage(uiLanguage) ?? parseAssistantLanguage(preferredLanguage) ?? 'fi';
}

/**
 * Shared by the HTTP fallback (`useAssistantChat`) and the socket path
 * (`useAssistantRealtime`), so the two cannot disagree about what a settled
 * turn IS (#2587 review P1), which settled turns may have written something
 * (#2597), or which received fields are trustworthy enough to render (#2587).
 */

function primitiveRecord(value: unknown): value is Record<string, string | number | boolean> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every(
    (v) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
  );
}

/**
 * Absent on the wire: the key is missing. An explicit `null` is PRESENT and
 * malformed (#2587 review): only a missing key means a legacy turn.
 */
function absent(value: unknown): value is undefined {
  return value === undefined;
}

/**
 * A pending card's `presenter`, only if it still has the shape the card
 * renders. Malformed → none: the card then renders its `description`, which
 * is the pre-#2587 behaviour for a pending card.
 */
export function presenterOf(value: unknown): ConfirmationPresenter | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const { key, params } = value as { key?: unknown; params?: unknown };
  if (typeof key !== 'string' || key.length === 0) return undefined;
  return primitiveRecord(params) ? { key, params } : undefined;
}

/**
 * Stands in for a settled presenter that arrived malformed. Deliberately a key
 * no bundle phrases, so the settled line renders the GENERIC localised line
 * of its outcome — never the English summary in `content`.
 */
export const UNAVAILABLE_PRESENTER: ConfirmationPresenter = {
  key: 'confirmation.presenters.unavailable',
  params: {},
};

/** A settled turn's `presenter`: absent stays absent (a legacy row); malformed becomes the generic stand-in. */
export function settledPresenterOf(value: unknown): ConfirmationPresenter | undefined {
  if (absent(value)) return undefined;
  return presenterOf(value) ?? UNAVAILABLE_PRESENTER;
}

/** Stands in for a failure reason that arrived malformed: the generic localised failure line. */
export const GENERIC_FAILURE: AssistantFailureReason = { code: 'action_failed', params: {} };

/**
 * A received or persisted `failure` (#2587). Absent stays absent (a legacy
 * row renders as before); PRESENT but malformed — `params: { name: null }`, a
 * non-string code — becomes the generic failure, because the turn it rides on
 * is a structured one whose `content` is the model's English.
 */
export function failureOf(value: unknown): AssistantFailureReason | undefined {
  if (absent(value)) return undefined;
  if (typeof value !== 'object' || value === null) return GENERIC_FAILURE;
  const { code, params } = value as { code?: unknown; params?: unknown };
  if (typeof code !== 'string' || code.length === 0) return GENERIC_FAILURE;
  return primitiveRecord(params) ? { code, params } : GENERIC_FAILURE;
}

const ACTION_OUTCOMES: readonly AssistantActionOutcome[] = ['ok', 'failed', 'unknown'];

/**
 * A confirmed turn's `outcome`. Absent → nothing (a legacy turn); PRESENT but
 * not one of the three — `null`, a garbage string — → `'unknown'`, never a
 * success, and never a fall-through to the raw English content (#2587 review).
 */
export function confirmedOutcomeOf(value: unknown): AssistantActionOutcome | undefined {
  if (absent(value)) return undefined;
  return typeof value === 'string' && (ACTION_OUTCOMES as readonly string[]).includes(value)
    ? (value as AssistantActionOutcome)
    : 'unknown';
}

/**
 * A cancelled turn's `outcome` (#2597). The only value a cancel carries is
 * `'unknown'`; ANY present value is read as that, because the conservative
 * mistake is a needless refresh, and the other one is a stale list after a
 * write that may have committed.
 */
export function cancelledOutcomeOf(value: unknown): 'unknown' | undefined {
  return absent(value) ? undefined : 'unknown';
}

type SettledType = 'action_confirmed' | 'action_cancelled';

export interface SettledTurn {
  /** #2821: the receipt time a quote acceptance sent. */
  pinnedReceivedAt?: string;
  /** #2821: a replay of an action completed earlier. */
  alreadyCompleted?: boolean;
  readonly quoteResult?: QuoteResultAction;
  readonly type: SettledType;
  readonly actionId: string;
  readonly outcome?: AssistantActionOutcome;
  readonly presenter?: ConfirmationPresenter;
  readonly failure?: AssistantFailureReason;
  /** The server named no settled type this client knows; treated as a possible write. */
  readonly unrecognised: boolean;
}

/**
 * What a settled reply or event IS, from its own fields (#2587 review P1).
 *
 * `/cancel` may REPLAY a confirmation another tab already spent — the server
 * answers `action_confirmed, outcome: 'ok'` — and `/confirm` may replay a
 * cancel. The verb the user tapped says nothing about which: the reply's own
 * `type`, `outcome` and `actionId` do. `fallbackType` is only for a reply
 * whose type this client does not recognise, which is then also treated as a
 * possible write.
 */
const PINNED_RECEIVED_AT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/;

/** A settled quote acceptance's sent receipt time (#2821), or undefined when absent/malformed. */
export function pinnedReceivedAtOf(value: unknown): string | undefined {
  return typeof value === 'string' && PINNED_RECEIVED_AT.test(value) ? value : undefined;
}

export function settledTurnOf(
  response: {
    type?: unknown;
    actionId?: unknown;
    outcome?: unknown;
    presenter?: unknown;
    failure?: unknown;
    pinnedReceivedAt?: unknown;
    alreadyCompleted?: unknown;
    quoteResult?: unknown;
    conversationId?: unknown;
  },
  fallback: { type: SettledType; actionId: string }
): SettledTurn {
  const known = response.type === 'action_confirmed' || response.type === 'action_cancelled';
  const type: SettledType = known ? (response.type as SettledType) : fallback.type;
  const actionId =
    typeof response.actionId === 'string' && response.actionId.length > 0 ? response.actionId : fallback.actionId;
  const outcome =
    type === 'action_cancelled' ? cancelledOutcomeOf(response.outcome) : confirmedOutcomeOf(response.outcome);
  const presenter = settledPresenterOf(response.presenter);
  const failure = type === 'action_confirmed' ? failureOf(response.failure) : undefined;
  const pinnedReceivedAt =
    type === 'action_confirmed' ? pinnedReceivedAtOf(response.pinnedReceivedAt) : undefined;
  const quoteResult =
    type === 'action_confirmed'
      ? boundQuoteResult(response.quoteResult, {
          outcome,
          conversationId: response.conversationId,
          actionId,
        })
      : undefined;
  return {
    type,
    actionId,
    ...(quoteResult ? { quoteResult } : {}),
    ...(outcome ? { outcome } : {}),
    ...(presenter ? { presenter } : {}),
    ...(failure ? { failure } : {}),
    ...(pinnedReceivedAt ? { pinnedReceivedAt } : {}),
    ...(type === 'action_confirmed' && response.alreadyCompleted === true ? { alreadyCompleted: true } : {}),
    unrecognised: !known,
  };
}

/**
 * Whether a settled turn may have written something the lists show.
 *
 * Every `action_confirmed` may (an `unknown` one most of all), and so may a
 * cancel the server marks `outcome: 'unknown'`: it was tapped after a
 * confirmed attempt that is still in flight and may yet commit (#2597). A
 * plain cancel wrote nothing.
 */
export function mayHaveWrittenTurn(
  type: AssistantMessage['type'],
  outcome: AssistantActionOutcome | undefined
): boolean {
  if (type === 'action_confirmed') return true;
  return type === 'action_cancelled' && outcome === 'unknown';
}

/**
 * Every query family a gated assistant write can touch — a conservative
 * superset rather than a per-tool map, because a replay or a second tab can
 * settle any tool on any path:
 *
 *  - persons, companies, vehicles: the create/update customer and vehicle ports;
 *  - bookings, workOrders, workOrderHistory: create, reschedule, assign,
 *    confirm and status ports, and every capacity resolution.
 *    `workOrderHistory` is the legacy `work-orders` prefix `useUndoWorkOrder`
 *    invalidates history under (`workOrders` is `['workOrders']`);
 *  - lifts: a booking change moves lift status and availability;
 *  - search: global search caches persons, companies, vehicles and work
 *    orders for five minutes;
 *  - workOrderParts, invoicePreview: `add_work_order_item` changes the lines
 *    and the billing preview;
 *  - approvals: approve / decline / escalate;
 *  - exceptions: `execute_capacity_resolution` resolves the case;
 *  - inventory: `suggest_reorder` (procurement recommendations have no portal
 *    query of their own; the inventory views show them);
 *  - dashboard: its counters read all of the above.
 *
 * Invalidation is not reconciliation: inactive queries only go stale, a
 * refetch can fail, and a read can lag the write. Uncertainty copy stands
 * regardless of what comes back.
 */
export const ASSISTANT_WRITE_QUERY_FAMILIES: readonly QueryKey[] = [
  queryKeys.persons.all,
  queryKeys.companies.all,
  queryKeys.vehicles.all,
  queryKeys.bookings.all,
  queryKeys.workOrders.all,
  queryKeys.workOrderHistory.all,
  queryKeys.lifts.all,
  queryKeys.search.all,
  queryKeys.workOrderParts.all,
  queryKeys.invoicePreview.all,
  queryKeys.approvals.all,
  queryKeys.exceptions.all,
  queryKeys.inventory.all,
  queryKeys.dashboard.all,
];

export function invalidateAssistantWriteCaches(queryClient: QueryClient): void {
  for (const queryKey of ASSISTANT_WRITE_QUERY_FAMILIES) {
    void queryClient.invalidateQueries({ queryKey });
  }
}
